<template>
  <div ref="rootEl" class="w-full overflow-hidden">
    <div :style="{ paddingLeft: `${insetLeft}px`, paddingRight: `${insetRight}px` }">
      <div class="flex items-center w-full" aria-hidden="true">
        <template v-for="(step, index) in steps" :key="step">
          <div
            data-testid="create-schedule-step-indicator"
            class="w-4 h-4 shrink-0 rounded-full"
            :class="current >= index ? 'bg-primary30' : 'bg-gray-300'"
          ></div>
          <div
            v-if="index < steps.length - 1"
            class="flex-grow h-[2px] mx-2"
            :class="current > index ? 'bg-primary30' : 'bg-gray-300'"
          ></div>
        </template>
      </div>
      <span v-if="hideLabels" class="sr-only">Etapa {{ current + 1 }} de {{ steps.length }}</span>
      <ol
        v-if="!hideLabels"
        aria-label="Etapas da criação de escala"
        class="grid list-none p-0 mt-2 mb-0"
        :style="labelGridStyle"
      >
        <li
          v-for="(step, index) in steps"
          :key="step"
          class="flex justify-center px-0.5"
          :aria-current="current === index ? 'step' : undefined"
        >
          <span
            data-testid="create-schedule-step-label"
            class="w-fit leading-tight text-center break-normal hyphens-none"
            :class="[narrow ? 'text-xs' : 'text-sm', labelColorClass(index)]"
          >
            {{ step }}
          </span>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    steps: string[]
    current: number
    /** Só o trilho com as bolinhas (modal de convite, D31). */
    hideLabels?: boolean
  }>(),
  { hideLabels: false }
)

// Abaixo desta largura os nomes descem para text-xs (Q12).
const NARROW_WIDTH_PX = 840
const DOT_RADIUS_PX = 8

const rootEl = ref<HTMLElement | null>(null)
const insetLeft = ref(0)
const insetRight = ref(0)
const narrow = ref(false)

// Uma coluna por etapa, da largura do vão entre bolinhas e centrada em cada bolinha.
// As colunas das pontas avançam sobre o recuo do trilho, mas o texto não passa dele.
const labelGridStyle = computed(() => {
  const count = props.steps.length
  const gap = count > 1 ? `((100% - ${2 * DOT_RADIUS_PX}px) / ${count - 1})` : '100%'
  return {
    width: `calc(${count} * ${gap})`,
    marginLeft: `calc(${DOT_RADIUS_PX}px - ${gap} / 2)`,
    gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`
  }
})

function labelColorClass(index: number) {
  if (index === props.current) return 'font-bold text-primary30'
  return index < props.current ? 'text-primary30' : 'text-gray-400'
}

// Ref em v-for não garante a ordem do array no Vue 3: as pontas vêm do DOM.
function edgeLabels(): HTMLElement[] {
  const labels = rootEl.value?.querySelectorAll<HTMLElement>(
    '[data-testid="create-schedule-step-label"]'
  )
  return labels?.length ? [labels[0], labels[labels.length - 1]] : []
}

// O trilho recua só meia largura do nome de cada ponta: as bolinhas ficam
// equidistantes e nenhum nome centralizado passa da borda.
function measure() {
  const labels = edgeLabels()
  if (rootEl.value) narrow.value = rootEl.value.clientWidth < NARROW_WIDTH_PX
  if (!labels.length) return
  insetLeft.value = Math.max(0, labels[0].offsetWidth / 2 - DOT_RADIUS_PX)
  insetRight.value = Math.max(0, labels[labels.length - 1].offsetWidth / 2 - DOT_RADIUS_PX)
}

let frame: number | undefined
function scheduleMeasure() {
  if (frame !== undefined) cancelAnimationFrame(frame)
  frame = requestAnimationFrame(() => {
    frame = undefined
    measure()
  })
}

let observer: ResizeObserver | undefined
function observe() {
  observer?.disconnect()
  if (!observer || !rootEl.value) return
  observer.observe(rootEl.value)
  edgeLabels().forEach((label) => observer?.observe(label))
}

onMounted(() => {
  if (typeof ResizeObserver !== 'undefined') observer = new ResizeObserver(scheduleMeasure)
  observe()
  measure()
})

watch([() => props.current, () => props.steps], async () => {
  await nextTick()
  observe()
  scheduleMeasure()
})

onBeforeUnmount(() => {
  observer?.disconnect()
  if (frame !== undefined) cancelAnimationFrame(frame)
})
</script>
